import {
  Component,
  inject,
  signal,
  viewChild,
  ElementRef,
  Signal,
  WritableSignal
} from '@angular/core';

import { TranslatePipe } from '../../../pipes/translate.pipe';
import { IconComponent } from '../../icon/icon.component';
import { FocusOnDisabledDirective } from '../../focus/focus-on-disabled.directive';
import { DatePickerButtonComponent } from '../date-picker-button/date-picker-button.component';
import { DatePickerInnerComponent } from '../date-picker-inner/date-picker-inner.component';

import type { DateObject } from '../date-picker-inner/date-picker-inner.domain';
import type { OnInit } from '@angular/core';

@Component({
  selector: 'td-wb-month-picker',
  templateUrl: './month-picker.component.html',
  styleUrls: ['./month-picker.component.scss'],
  imports: [DatePickerButtonComponent, FocusOnDisabledDirective, IconComponent, TranslatePipe]
})
export class MonthPickerComponent implements OnInit {

  /**
   * Number of months in year
   */
  private static readonly NUMBER_MONTHS_IN_YEAR: number = 12;

  readonly yearButtonRef: Signal<ElementRef | undefined> = viewChild<ElementRef>('yearButton');

  // ── DI ──────────────────────────────────────────────────────────────────
  protected readonly datePicker = inject(DatePickerInnerComponent);

  /**
   * Title of month view
   */
  readonly title: WritableSignal<string | undefined> = signal<string | undefined>(undefined);

  /**
   * Array of months to display
   */
  readonly rows: WritableSignal<Array<Array<DateObject>>> = signal<Array<Array<DateObject>>>([]);

  /**
   * Boolean for determining whether previous button should be disabled
   */
  readonly navPrevDisabled: WritableSignal<boolean> = signal<boolean>(false);

  /**
   * Boolean for determining whether next button should be disabled
   */
  readonly navNextDisabled: WritableSignal<boolean> = signal<boolean>(false);

  /**
   * This function registers handlers and initializes step
   */
  ngOnInit(): void {
    this.datePicker.step.set('month', { years: 1 });
    this.datePicker.setRefreshViewHandler((): void => this.refreshView(), 'month');
    this.datePicker.setCompareHandler((date1: Date, date2: Date): number => this.compareDates(date1, date2), 'month');
    this.datePicker.refreshView();
  }

  /**
   * This function is the refresh view handler for date picker inner component
   */
  refreshView(): void {
    const datePicker = this.datePicker;
    const months = new Array<DateObject>(MonthPickerComponent.NUMBER_MONTHS_IN_YEAR);
    const year: number = datePicker.activeDate.getFullYear();
    let date: Date;

    for (let i = 0; i < MonthPickerComponent.NUMBER_MONTHS_IN_YEAR; i++) {
      date = new Date(year, i, 1);
      months[i] = datePicker.createDateObject(date, datePicker.formatMonth());
      months[i].uid = datePicker.uniqueId + '-' + i;
    }

    // disable navigation according to the range (maxDate and minDate)
    this.navPrevDisabled.set((datePicker.minDate() !== undefined) && (months[0].date <= datePicker.minDate()));
    this.navNextDisabled.set((datePicker.maxDate() !== undefined) && (new Date(year + 1, 0, 1) > datePicker.maxDate()));

    this.title.set(datePicker.formatDate(datePicker.activeDate, datePicker.formatMonthTitle()));
    this.rows.set(datePicker.split(months, 3) as Array<Array<DateObject>>);
    datePicker.updateActiveDateId(months);
  }

  /**
   * This function is the compare handler for date picker inner component
   *
   * @param date1 Date to compare
   * @param date2 Date to compare
   * @returns Returns comparison of two dates
   */
  compareDates(date1: Date, date2: Date): number {
    const d1 = new Date(date1.getFullYear(), date1.getMonth());
    const d2 = new Date(date2.getFullYear(), date2.getMonth());
    return d1.getTime() - d2.getTime();
  }

}
